import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { AboutPageSchema, BreadcrumbListSchema, WebPageSchema } from '@/components/feature/SchemaOrg';
import { aboutInfo } from '@/mocks/yihao';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch('https://readdy.ai/api/form/d9u07cd3pcjqs2fcsju0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <AboutPageSchema />
      <WebPageSchema
        title="关于我们 - 壹号娱乐"
        description={`壹号娱乐成立于${aboutInfo.founded}，是值得信赖的综合娱乐平台。${aboutInfo.mission}`}
        url="https://bukedg.cn/about"
      />
      <BreadcrumbListSchema />

      <Navbar />
      <Breadcrumb items={[{ label: '关于我们' }]} />

      <main>
        <section className="w-full px-6 md:px-10 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden h-[240px] md:h-[360px] mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=Luxurious%20corporate%20headquarters%20interior%20with%20warm%20golden%20lighting%2C%20elegant%20modern%20design%2C%20marble%20floors%2C%20sophisticated%20atmosphere%2C%20premium%20office%20space%2C%20no%20people&width=1440&height=600&seq=about-hero&orientation=landscape"
                alt="壹号娱乐品牌介绍"
                title="壹号娱乐品牌介绍"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-2">关于壹号娱乐</h1>
                <p className="text-white/80 text-sm md:text-base max-w-2xl">值得信赖的综合娱乐平台，为您提供极致的娱乐体验</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-16">
              <div className="lg:col-span-2">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">品牌介绍</h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    壹号娱乐成立于{aboutInfo.founded}，总部位于{aboutInfo.location}。作为一家领先的综合娱乐平台，我们致力于为全球用户提供安全、公平、优质的多元化娱乐体验。
                  </p>
                  <p>
                    经过多年的发展，壹号娱乐已建立起涵盖真人娱乐、电子游艺、体育赛事、彩票游戏、捕鱼游戏等在内的全方位娱乐内容矩阵。平台注册用户已突破500万，每日活跃用户数量持续增长，在业内赢得了良好的口碑和广泛的认可。
                  </p>
                  <p>
                    <strong>我们的使命：</strong>{aboutInfo.mission}
                  </p>
                  <p>
                    <strong>我们的愿景：</strong>{aboutInfo.vision}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 content-start">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="rounded-xl bg-background-100 p-5 text-center border border-background-200/60">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                    <div className="text-foreground-500 text-xs">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">核心价值观</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                {aboutInfo.values.map((v, idx) => (
                  <div key={v.title} className="rounded-xl bg-background-100 p-5 border border-background-200/60 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-500 font-heading font-bold text-lg">{idx + 1}</span>
                    </div>
                    <h3 className="font-heading font-bold text-base text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-foreground-500 text-xs leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">信誉与资质</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutInfo.reputation.map((rep) => (
                  <div key={rep.title} className="rounded-xl bg-background-100 p-6 border border-background-200/60 hover:border-primary-200 transition-colors cursor-pointer">
                    <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                      <i className={`${rep.icon} text-primary-500 text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-bold text-base text-foreground-900 mb-2">{rep.title}</h3>
                    <p className="text-foreground-500 text-xs leading-relaxed">{rep.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">联系我们</h2>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-phone-line', label: '客服电话', value: aboutInfo.contact.phone },
                    { icon: 'ri-mail-line', label: '电子邮箱', value: aboutInfo.contact.email },
                    { icon: 'ri-map-pin-line', label: '运营中心', value: aboutInfo.contact.address },
                    { icon: 'ri-wechat-line', label: '官方微信', value: aboutInfo.contact.wechat },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-primary-500 text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-foreground-400 text-xs mb-0.5">{item.label}</p>
                        <p className="text-foreground-800 font-medium text-sm">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">在线留言</h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 rounded-xl bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
                      <div className="flex items-center gap-2">
                        <i className="ri-check-line text-lg"></i>
                        <span>留言已成功提交，我们将尽快与您联系！</span>
                      </div>
                    </div>
                  )}

                  {formStatus === 'error' && formError && (
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                      <div className="flex items-center gap-2">
                        <i className="ri-error-warning-line text-lg"></i>
                        <span>{formError}</span>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}